import React, { useContext, useEffect, useMemo, useState } from "react";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Save, ArrowLeft } from "lucide-react";

const API = "https://nissi-india.com/api/companyMaster";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

// yyyy-MM-dd  →  dd/MM/yyyy
const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

// dd/MM/yyyy  →  yyyy-MM-dd
const ddmmyyyyToISO = (s) => {
  if (!s) return "";
  const [dd, mm, yyyy] = s.split("/");
  return `${yyyy}-${mm}-${dd}`;
};


// dd/MM/yyyy helper
const isValidDDMMYYYY = (s) => {
  if (!s) return false;
  const parts = String(s).trim().split("/");
  if (parts.length !== 3) return false;
  const [dd, mm, yyyy] = parts.map((x) => parseInt(x, 10));
  if (!dd || !mm || !yyyy) return false;
  const d = new Date(yyyy, mm - 1, dd);
  return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
};

const parseDDMMYYYY = (s) => {
  const [dd, mm, yyyy] = String(s).trim().split("/").map((x) => parseInt(x, 10));
  return new Date(yyyy, mm - 1, dd);
};

const CompanyStatutoryAdd = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const statutoryId = Number(params.get("statutoryId") || 0);

  // In ASPX they always use companyid=1
  // In your React app, you already have company master. If you store companyId in localStorage, read it.
  // For now, keep it same as ASPX:
  const companyId = 1;

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    statutoryHead: "",
    statutoryDetail: "",
    dateIssued: "",
    dateExpired: "",
    remarks: "",
  });

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const titleText = useMemo(
    () => (statutoryId ? "COMPANY STATUTORY - EDIT" : "COMPANY STATUTORY - ADD"),
    [statutoryId]
  );

  const loadForEdit = async () => {
    if (!statutoryId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/company/${companyId}/statutory/${statutoryId}`, {
        headers: tenantHeaders(),
        params: tenantParams(),
      });      const d = r.data.data;

      setForm({
        statutoryHead: d.statutoryHead || "",
        statutoryDetail: d.statutoryDetail || "",
        dateIssued: d.dateIssued || "",
        dateExpired: d.dateExpired || "",
        remarks: d.remarks || "",
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to load statutory details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statutoryId]);

  const validate = () => {
    if (!form.statutoryHead.trim()) return toast.error("Please enter Statutory head"), false;
    if (!form.statutoryDetail.trim()) return toast.error("Please enter Statutory Description"), false;
    if (!form.dateIssued.trim()) return toast.error("Please enter Issued date"), false;
    if (!form.dateExpired.trim()) return toast.error("Please enter Expiry date"), false;

    if (!isValidDDMMYYYY(form.dateIssued)) return toast.error("Issued date must be dd/MM/yyyy"), false;
    if (!isValidDDMMYYYY(form.dateExpired)) return toast.error("Expiry date must be dd/MM/yyyy"), false;

    const issued = parseDDMMYYYY(form.dateIssued);
    const expiry = parseDDMMYYYY(form.dateExpired);

    if (expiry.getTime() < issued.getTime()) {
      return toast.error("Please select date issued less than expiry date"), false;
    }

    return true;
  };

  const onSave = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        statutoryId: statutoryId || 0,
        statutoryHead: form.statutoryHead,
        statutoryDetail: form.statutoryDetail,
        dateIssued: form.dateIssued,
        dateExpired: form.dateExpired,
        remarks: form.remarks,
      };

      const r = await axios.post(`${API}/company/${companyId}/statutory/save`, payload, {
        headers: tenantHeaders(),
      });
      if (r.data.ok) {
        toast.success(r.data.mode === "insert" ? "Inserted Successfully" : "Updated Successfully");

        // Back to CompanyMaster Statutory tab (same flow)
        navigate("/adminMaster/companyMaster");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.error || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{titleText}</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <button
              onClick={() => navigate("/adminMaster/companyMaster?tab=statutory")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${
                isDark ? "border-white/10 bg-white/5 hover:bg-white/10" : "border-gray-200 bg-gray-50 hover:bg-white"
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {loading && (
              <div className={`${isDark ? "text-white/70" : "text-gray-600"} text-sm`}>
                Working...
              </div>
            )}
          </div>

          {/* FORM */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Statutory Head */}
            <div>
              <label className="text-sm font-semibold">Statutory Head *</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.statutoryHead}
                onChange={(e) => setField("statutoryHead", e.target.value)}
              />
            </div>

            {/* Statutory Description */}
            <div>
              <label className="text-sm font-semibold">Statutory Description *</label>
              <input
                className="w-full mt-1 p-2 rounded-lg border"
                value={form.statutoryDetail}
                onChange={(e) => setField("statutoryDetail", e.target.value)}
              />
            </div>

            {/* Date Issued */}
          <div>
  <label className="text-sm font-semibold">Date Issued *</label>
  <input
    type="date"
    className="w-full mt-1 p-2 rounded-lg border"
    value={ddmmyyyyToISO(form.dateIssued)}
    onChange={(e) =>
      setField("dateIssued", isoToDDMMYYYY(e.target.value))
    }
  />
</div>

            {/* Expiry Date */}
           <div>
  <label className="text-sm font-semibold">Expiry Date *</label>
  <input
    type="date"
    className="w-full mt-1 p-2 rounded-lg border"
    min={ddmmyyyyToISO(form.dateIssued)}   // prevents earlier date
    value={ddmmyyyyToISO(form.dateExpired)}
    onChange={(e) =>
      setField("dateExpired", isoToDDMMYYYY(e.target.value))
    }
  />
</div>


            {/* Remarks */}
            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Remarks</label>
              <textarea
                className="w-full mt-1 p-2 rounded-lg border"
                rows={3}
                value={form.remarks}
                onChange={(e) => setField("remarks", e.target.value)}
              />
            </div>
          </div>

          {/* SAVE BUTTON */}
          <div className="mt-8 flex justify-center">
            <button
              disabled={loading}
              onClick={onSave}
              className={`
                relative group
                px-10 py-3 rounded-xl font-semibold text-white tracking-wide
                ${buttonClass} ${hoverClass}
                shadow-lg transition-all duration-300
                hover:shadow-2xl hover:-translate-y-0.5
                disabled:opacity-60 disabled:cursor-not-allowed
              `}
            >
              <span
                className={`
                  absolute inset-0 rounded-xl opacity-0 group-hover:opacity-20
                  transition-opacity duration-300 ${buttonClass} blur-lg
                `}
              />
              <span className="relative flex items-center gap-2">
                <Save className="w-4 h-4" />
                {statutoryId ? "Update" : "Submit"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default CompanyStatutoryAdd;
